export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const response = await fetch(path, { ...init, headers, credentials: 'include' });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof payload?.error === 'string'
      ? payload.error
      : payload?.error?.message || `请求失败（${response.status}）`;
    throw new ApiError(response.status, message);
  }
  return payload as T;
}

export interface Viewer {
  id: string;
  numId?: number | null;
  email: string;
  displayName: string;
  avatar?: string;
  profile: {
    school?: string;
    grade?: string;
    major?: string;
    interests?: string[];
    weakSubjects?: string[];
    bio?: string;
  };
  settings?: Record<string, unknown>;
  scope?: 'full' | 'read';
  isAdmin?: boolean;
  hasPassword?: boolean;
  createdAt?: string;
}

export interface Notice {
  id: string;
  title: string;
  source?: string;
  sourceUrl?: string;
  summary?: string;
  raw?: string;
  date?: string;
  due?: string;
  time?: string;
  createdAt?: string;
  used?: boolean;
}

export interface FocusItem {
  id: string;
  kind: 'event' | 'todo';
  time: string;
  title: string;
  subtitle?: string;
  priority: number;
}

export interface HomeData {
  viewer: Viewer;
  today: string;
  range?: { from: string; to: string };
  overview: {
    importantEvents: number;
    openTodos: number;
    unreadNotifications: number;
    overdueTodos?: number;
    dueTodayTodos?: number;
    doneTodayTodos?: number;
  };
  todoStats?: {
    open: number;
    done: number;
    overdue: number;
    dueToday: number;
    dueThisWeek: number;
    doneToday: number;
    todayProgress: number | null;
    pressure: 'overdue' | 'today' | 'planned' | 'clear';
  };
  focus: FocusItem[];
  latestNotification: Notice | null;
}

export interface WhaleuEntry {
  id: string | number;
  title: string;
  summary?: string;
  url?: string;
  kind?: string;
  category?: string;
  date?: string;
  publishTime?: string;
  source?: string;
  sourceName?: string;
  audience?: string;
  college?: string;
  tags?: string[];
  match?: Array<'college' | 'audience' | 'topic' | string>;
  score?: number;
}

export interface WhaleuFeed {
  backend: 'postgres' | 'sqlite' | 'sqlite-fallback' | 'unavailable' | string;
  items: WhaleuEntry[];
  warning?: string;
  personalized?: boolean;
  rules?: Partial<WhaleuRules>;
}

export interface WhaleuRules {
  audience: string[];
  college: string[];
  topic: string[];
  deliver: boolean;
}

export interface WhaleuStats {
  backend: string;
  available: boolean;
  sources?: number;
  entries?: number;
  refs?: number;
  subscriptions?: number;
  error?: string;
}

export interface WhaleuSource {
  id: string;
  name: string;
  kind?: string;
  biz?: string;
  level?: number;
  verified?: boolean;
}

export interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  time?: string;
  location?: string;
  priority: number;
  done?: boolean;
  categories?: string[];
}

export interface CalendarTodo {
  id: string;
  title: string;
  due?: string;
  content?: string;
  priority: number;
  done: boolean;
  categories?: string[];
}

export interface CalendarData {
  events: CalendarEvent[];
  todos: CalendarTodo[];
  inbox: Notice[];
}

export interface UserDataStatus {
  storage?: string;
  authorizations?: {
    courses?: boolean;
    roster?: boolean;
    grades?: boolean;
    grantedAt?: string;
    revokedAt?: string | null;
  } | null;
}

export interface AcademicCourse {
  term?: string;
  courseCode?: string;
  courseName: string;
  weekday?: number | null;
  startPeriod?: number | null;
  endPeriod?: number | null;
  location?: string;
  teacher?: string;
}

export interface AcademicGoals {
  targetGpa?: number | null;
  targetScholarship?: string | null;
  note?: string | null;
}

export interface AcademicIdentity {
  college?: string | null;
  major?: string | null;
  grade?: string | null;
  level?: string | null;
  enrollYear?: number | null;
  gradeLevel?: number | null;
  rankPercent?: number | null;
  rankTotal?: number | null;
  rankApprox?: number | null;
  rankSource?: string | null;
  cet4?: number | null;
  cet6?: number | null;
  avgGpa?: number | null;
  disciplined?: boolean | null;
  physicalTest?: string | null;
  dailyBehavior?: string | null;
}

export interface CaigenSettings {
  dailyDigest?: boolean;
  pomodoro?: boolean;
  digestHour?: number;
  quietHours?: string;
  [key: string]: unknown;
}

export interface AuthTokenRecord {
  id: string;
  scope: 'full' | 'read';
  label?: string;
  userAgent?: string;
  sourceIp?: string;
  createdAt?: string;
  lastUsedAt?: string;
  expiresAt?: string;
  current?: boolean;
  expired?: boolean;
  revokedAt?: string | null;
}

export interface ConnectorGuide {
  version: string;
  title: string;
  summary: string;
  requirements: Array<{ label: string; value: string }>;
  steps: Array<{ n: number; title: string; detail: string; code?: string | null }>;
  advanced?: { title: string; detail: string; code: string };
  scopes: Array<{ key: string; label: string; detail: string }>;
  alternatives: Array<{ name: string; detail: string }>;
  troubleshooting: Array<{ q: string; a: string }>;
  privacy: string[];
}

export interface DigestPreview {
  title?: string;
  summary?: string;
  items?: WhaleuEntry[];
  generatedAt?: string;
  [key: string]: unknown;
}

export interface GoalCriterion {
  label: string;
  current?: string | number | boolean | null;
  required?: string | number | boolean | null;
  status: 'ok' | 'gap' | 'unknown' | 'pending';
}

export interface GoalAnalysisItem {
  key: string;
  name: string;
  category?: string;
  status: 'ok' | 'gap' | 'unknown' | 'pending';
  policyPending?: boolean;
  criteria?: GoalCriterion[];
  advice?: string | string[];
}

export interface GoalAnalysis {
  goals: GoalAnalysisItem[];
  profile?: AcademicProfile;
}

export interface AcademicProfile {
  gpa: number | null;
  average: number | null;
  credits: { earned: number; required: number | null; progress: number | null };
  gpaTrend: Array<{ term: string; courses: number; credits: number; gpa: number | null; average: number | null }>;
  plan: { name?: string; major?: string; grade?: string; pyfadm?: string } | null;
  currentCourses: AcademicCourse[];
  youth: {
    volunteerHours?: number | null;
    activityCount?: number | null;
    completionRate?: number | null;
    fiveEducation?: {
      five?: Record<string, number>;
      lao?: Record<string, number>;
      practice?: Record<string, number>;
    } | null;
  } | null;
  courseCount: number;
  termsCount?: number;
  goals?: AcademicGoals | null;
  identity?: AcademicIdentity | null;
  failed?: { count: number; courses?: Array<{ courseName?: string; score?: number | null }> };
  weak?: Array<{ name?: string; score?: number | null; credit?: number | null; term?: string }>;
  planBreakdown?: Array<{ group?: string; required?: number | null }>;
  earnedByType?: Array<{ type?: string; credits?: number | null; courses?: number | null }>;
}

export interface CalendarTerms {
  terms: Record<string, string>;
}

export interface CourseImportResult {
  term: string | null;
  courses: number;
  created: number;
  skipped: number;
  unscheduled: string[];
}

export interface AssignmentSyncResult {
  fetched: number;
  delivered: number;
  skipped: number;
}

export interface RuleEntry {
  id: string | number;
  title: string;
  content?: string;
  url?: string;
  category?: string;
  dept?: string;
  date?: string;
  tags?: string[];
}

export interface RuleSearchResult {
  backend: string;
  items: RuleEntry[];
}

export interface RuleStats {
  backend: string;
  available: boolean;
  entries?: number;
  categories?: number;
  depts?: number;
}
